import { Visibility } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import moment from 'moment/moment';
import React, { PropsWithChildren } from 'react';

import CopyOnClick from '#app/Component/CopyOnClick';
import IconAction from '#app/Component/IconAction';
import ShadowBoxComponent from '#app/Component/ShadowBoxComponent';
import TitleComponent from '#app/Component/TitleComponent';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { momentDateFormatter } from '#app/Util/MomentDateFormatter';
import { formatNumberToEurCurrency } from '#app/Util/NumberTool';
import {
  DisputeItem,
  DisputeState,
  DisputeStateTranslations,
  DisputeTypeTranslations,
} from '#app/typings/orderClientLitige';

type Props = {
  dispute: DisputeItem;
};

const OrderClientLitige: React.FC<PropsWithChildren<Props>> = ({ dispute, children }) => {
  const classes = useStyles();

  return (
    <ShadowBoxComponent className={classes.box}>
      <TitleComponent title="Information litige" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <div>
            <b>Référence Litige :</b> <CopyOnClick>{dispute.ref}</CopyOnClick>
          </div>
          <div>
            <b>Référence Commande :</b> <CopyOnClick>{dispute.orderClient.number}</CopyOnClick>
            <IconAction
              className={classes.icon}
              title="Visualisation la commande"
              icon="view"
              to={getRoutePathname('sale_order_client', { uuid: dispute.orderClient.uuid })}
            />
          </div>
          {dispute.orderClient.numberSylius && (
            <div>
              <b>Numero Sylius</b> : <CopyOnClick>{dispute.orderClient.numberSylius ?? '-'}</CopyOnClick>
              <a
                href={`https://www.eiyolab.com/terradmin/orders/${dispute.orderClient.numberSylius}`}
                target="_blank"
                rel="noreferrer"
              >
                <IconButton>
                  <Visibility className={classes.icon} style={{ color: '#9c27b0' }} />
                </IconButton>
              </a>
            </div>
          )}
          <div>
            <b>Transporteur :</b> {dispute.orderClient?.orderClientCarrier?.label}
          </div>
          <div>
            <b>Type de litige :</b> {dispute && DisputeTypeTranslations[dispute.type]}
          </div>
          <div>
            <b>État:</b> {dispute && DisputeStateTranslations[dispute.state]}
            {dispute.state === DisputeState.CLOSED && (
              <>
                <b> par : </b> {dispute.authorClosing?.name}
              </>
            )}
          </div>
          <div>
            <b>Auteur :</b> {dispute.authorLitige?.name}
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {dispute.reason && (
            <div>
              <b>Raison :</b> {dispute.reason}
            </div>
          )}
          <div>
            <b>Créée le :</b> {dispute && momentDateFormatter.dateTimeToFormatFr(dispute.createdAt)}
          </div>
          {dispute.dealAt && (
            <div>
              <b>Cloturé le :</b> {momentDateFormatter.dateTimeToFormatFr(dispute.dealAt)}
            </div>
          )}
          {dispute.commentClosing && (
            <div>
              <b>Commentaire litige envoyé au client :</b> {dispute.commentClosing}
            </div>
          )}
        </Grid>
        {dispute.returnAt && (
          <Grid size={{ xs: 12, md: 4 }} className={classes.border}>
            <b>Retour</b>
            <div>
              <b>Réceptionné par :</b> {dispute.authorLitigeReturn?.name}
            </div>
            <div>
              <b>le :</b> {moment(dispute.returnAt).format('lll')}
            </div>
            {dispute.commentReturn && (
              <div>
                <b>Commentaire :</b> {dispute.commentReturn}
              </div>
            )}
          </Grid>
        )}
        {dispute.forwardingAt && (
          <Grid size={{ xs: 12, md: 4 }} className={classes.border}>
            <b>Demande de réexpédition</b>
            <div>
              <b>Demandé par :</b> {dispute.authorForwarding?.name}
            </div>
            <div>
              <b>le :</b> {moment(dispute.forwardingAt).format('lll')}
            </div>
            {dispute.orderClient.orderClientForwarding && (
              <div>
                <b>Commande réexpédiée : </b>
                {dispute.orderClient.orderClientForwarding.number}
                <IconAction
                  className={classes.icon}
                  title="Visualisation la commande"
                  icon="view"
                  to={getRoutePathname('sale_order_client', {
                    uuid: dispute.orderClient.orderClientForwarding.uuid,
                  })}
                />
              </div>
            )}
          </Grid>
        )}
        {dispute.refundAskingAt && (
          <Grid size={{ xs: 12, md: 4 }} className={classes.border}>
            <b>Demande de remboursement</b>
            <div>
              <b>Demandé par :</b> {dispute.authorRefundAsking?.name}
            </div>
            <div>
              <b>le :</b> {moment(dispute.refundAskingAt).format('lll')}
            </div>
            <div>
              <b>Montant à rembourser :</b> {formatNumberToEurCurrency(dispute.amountToRefund)}
            </div>
            {dispute.repaidAt && (
              <>
                <div>
                  <b>Remboursé par :</b> {dispute.authorRepaid?.name}
                </div>
                <div>
                  <b>le :</b> {moment(dispute.repaidAt).format('lll')}
                </div>
              </>
            )}
          </Grid>
        )}
      </Grid>
      {children}
    </ShadowBoxComponent>
  );
};

const useStyles = makeStyles({
  box: {
    height: '100%',
    fontSize: 14,
    marginBottom: 15,
  },
  icon: {
    fontSize: '17px !important',
    position: 'absolute',
    right: -5,
    top: -2,
    color: '#17a2b8',
  },
  border: {
    border: '1px solid #bbb',
    padding: 10,
    borderRadius: 4,
  },
});

export default OrderClientLitige;
